import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, ShieldCheck } from "lucide-react";

/**
 * OTPVerifier — reusable email OTP verification block.
 * Renders inside a form; parent controls email input & submit.
 *
 * props:
 *  - email: string
 *  - purpose: 'contact' | 'application' | 'newsletter'
 *  - verified: boolean
 *  - verificationToken: string | null
 *  - onVerified: (token) => void
 *  - lightTheme: boolean (default false — dark)
 */
export default function OTPVerifier({ email, purpose, verified, verificationToken, onVerified, lightTheme = false }) {
    const [stage, setStage] = useState("idle"); // idle | sending | sent | verifying | verified
    const [code, setCode] = useState("");
    const [devCode, setDevCode] = useState(null);

    const inputCls = lightTheme ? "input-editorial-light" : "input-editorial";
    const labelClr = lightTheme ? "text-[color:#8F6F26]" : "text-[var(--gold)]";

    const sendCode = async () => {
        if (!email) {
            toast.error("Enter your email first");
            return;
        }
        setStage("sending");
        try {
            const { data } = await api.post("/otp/request", { email, purpose });
            if (data.dev_code) setDevCode(data.dev_code);
            setStage("sent");
            toast.success(data.email_sent ? "Verification code sent to your email" : "Verification code generated (email service pending)");
        } catch (err) {
            setStage("idle");
            toast.error("Could not send verification code");
        }
    };

    const verify = async () => {
        if (!code || code.length < 6) {
            toast.error("Enter the 6-digit code");
            return;
        }
        setStage("verifying");
        try {
            const { data } = await api.post("/otp/verify", { email, code, purpose });
            onVerified(data.verification_token);
            setStage("verified");
            toast.success("Email verified");
        } catch (err) {
            setStage("sent");
            const msg = err?.response?.data?.detail || "Incorrect code";
            toast.error(msg);
        }
    };

    if (verified) {
        return (
            <div className="flex items-center gap-3 label-mono text-[var(--emerald)]" data-testid="otp-verified">
                <ShieldCheck className="w-4 h-4" /> Email verified
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {stage === "idle" && (
                <button
                    type="button"
                    onClick={sendCode}
                    className="btn-ghost"
                    data-testid="otp-send-btn"
                >
                    <Mail className="w-4 h-4" /> Send verification code
                </button>
            )}
            {stage === "sending" && (
                <div className={`label-mono ${labelClr}`}>Sending code…</div>
            )}
            {(stage === "sent" || stage === "verifying") && (
                <div className="space-y-3">
                    <div className={`label-mono ${labelClr}`}>
                        Enter the 6-digit code sent to {email}
                        {devCode && <span className="ml-2 opacity-60">(dev: {devCode})</span>}
                    </div>
                    <div className="flex flex-wrap items-end gap-4">
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={6}
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                            placeholder="000000"
                            className={`${inputCls} max-w-[220px] font-mono tracking-[0.4em] text-lg`}
                            data-testid="otp-code-input"
                        />
                        <button
                            type="button"
                            onClick={verify}
                            disabled={stage === "verifying"}
                            className="btn-gold"
                            data-testid="otp-verify-btn"
                        >
                            {stage === "verifying" ? "Verifying…" : "Verify"}
                        </button>
                        <button
                            type="button"
                            onClick={sendCode}
                            className="label-mono underline underline-offset-4 opacity-70 hover:opacity-100"
                            data-testid="otp-resend-btn"
                        >
                            Resend
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
